import {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {usePage} from "@inertiajs/react";
import {useQueryBoolState, useQueryListState, useQueryState} from "./useQueryState.js";
import {friendlyErrorMessage, isReferenceYearComplete} from "./format.js";
import {gemerkteWerteLesen, gemerkteWerteSchreiben} from "../Filter/useGemerkteFilter.jsx";

/**
 * Gemeinsamer Filterzustand der Vertriebsauswertung (Zeitraum, Stichmonat,
 * Branche, Vertreter, Artikelart, Produktlinie, Farbton/Artikel, Fracht) - genutzt von
 * Gesamt, Vertreter × Monat, Vertreter und Graffiti vs. Fine Arts. Lädt
 * einmalig die Filteroptionen (GET /vertrieb/daten/filter) und setzt die
 * Voreinstellungen aus meta.default_years und den Branchen mit default=true,
 * sobald sie feststehen - außer der Nutzer hat über die URL oder die
 * Filterleiste bereits selbst gewählt (siehe Ref unten, analog
 * Statistics/useSalesFilters.js).
 *
 * T12: Ohne eigenen URL-Parameter (Menü-Klick, frischer Aufruf) greift statt
 * der serverseitigen Defaults zuerst der zuletzt gemerkte Filterzustand je
 * Benutzer (Local-Storage-Rückfall über die geteilten Lese-/Schreibfunktionen
 * aus Components/Filter/useGemerkteFilter.jsx) - ein mitgegebener
 * URL-Parameter hat weiterhin Vorrang (geteilter Link, Browser-Zurück).
 */
export default function useAnalysisFilters() {
    const [meta, setMeta] = useState(null);
    const [metaError, setMetaError] = useState(null);

    const benutzerId = usePage().props.auth?.id;
    const speicherSchluessel = `filter.vertrieb-analyse${benutzerId !== undefined && benutzerId !== null ? "." + benutzerId : ""}`;
    // Nur einmal beim ersten Rendern gelesen - genau wie useQueryState selbst
    // seinen initialValue nur beim ersten Rendern auswertet.
    const gemerkt = useMemo(() => gemerkteWerteLesen(speicherSchluessel), [speicherSchluessel]);

    const [yearFrom, setYearFrom] = useQueryState("year_from", gemerkt.year_from);
    const [yearTo, setYearTo] = useQueryState("year_to", gemerkt.year_to);
    const [stichmonat, setStichmonat] = useQueryState("stichmonat", gemerkt.stichmonat);
    const [sectors, setSectors] = useQueryListState("sectors", gemerkt.sectors || []);
    const [reps, setReps] = useQueryListState("reps", gemerkt.reps || []);
    const [articleTypes, setArticleTypes] = useQueryListState("article_types", gemerkt.article_types || []);
    const [productLines, setProductLines] = useQueryListState("product_lines", gemerkt.product_lines || []);
    const [articles, setArticles] = useQueryListState("articles", gemerkt.articles || []);
    const [withoutFreight, setWithoutFreight] = useQueryBoolState("without_freight", gemerkt.without_freight ?? false);

    // Ein gemerkter Zeitraum/Branchen-Stand zählt wie eine eigene Auswahl -
    // sonst würde der spätere Server-Default (default_years/default-Branchen)
    // die gerade erst wiederhergestellten Werte gleich wieder überschreiben.
    const hasExplicitYearsRef = useRef(Boolean(yearFrom || yearTo));
    const hasExplicitSectorsRef = useRef(sectors.length > 0);

    useEffect(() => {
        gemerkteWerteSchreiben(speicherSchluessel, {
            year_from: yearFrom,
            year_to: yearTo,
            stichmonat,
            sectors,
            reps,
            article_types: articleTypes,
            product_lines: productLines,
            articles,
            without_freight: withoutFreight,
        });
    }, [speicherSchluessel, yearFrom, yearTo, stichmonat, sectors, reps, articleTypes, productLines, articles, withoutFreight]);

    useEffect(() => {
        let active = true;
        axios
            .get("/vertrieb/daten/filter")
            .then(({data: response}) => {
                if (!active) {
                    return;
                }
                // Der Endpunkt liefert Listen (sectors, reps, …) auf oberster
                // Ebene und Datenstand/Zeitraum unter "meta" - beides wird hier
                // zu einem Objekt zusammengeführt.
                const data = {...response, ...(response?.meta || {})};
                setMeta(data);
                if (!hasExplicitYearsRef.current && Array.isArray(data?.default_years) && data.default_years.length) {
                    setYearFrom(String(Math.min(...data.default_years)));
                    setYearTo(String(Math.max(...data.default_years)));
                }
                if (!hasExplicitSectorsRef.current && Array.isArray(data?.sectors)) {
                    const defaults = data.sectors.filter((s) => s.default).map((s) => String(s.id));
                    if (defaults.length) {
                        setSectors(defaults);
                    }
                }
            })
            .catch((e) => {
                if (active) {
                    setMetaError(friendlyErrorMessage(e, "Die Filteroptionen konnten nicht geladen werden."));
                }
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const effectiveStichmonat = stichmonat || (meta?.stichmonat ? String(meta.stichmonat) : undefined);
    // Rein aus Zeitraum-Filter und laufendem Jahr berechnet (nicht aus der
    // Antwort eines einzelnen Daten-Endpunkts) - die Filterleiste kann den
    // Stichmonat damit sofort sperren, ohne auf eine Tabelle zu warten
    // (schnittstelle.md 7.2).
    const referenceComplete = isReferenceYearComplete(yearTo, meta?.current_year);

    const params = useMemo(
        () => ({
            year_from: yearFrom ? Number(yearFrom) : undefined,
            year_to: yearTo ? Number(yearTo) : undefined,
            stichmonat: effectiveStichmonat ? Number(effectiveStichmonat) : undefined,
            sectors,
            reps,
            article_types: articleTypes,
            product_lines: productLines,
            articles,
            without_freight: withoutFreight ? 1 : 0,
        }),
        [yearFrom, yearTo, effectiveStichmonat, sectors, reps, articleTypes, productLines, articles, withoutFreight]
    );

    const filtersReady = Boolean(meta && yearFrom && yearTo);

    return {
        meta,
        metaError,
        filtersReady,
        yearFrom,
        setYearFrom: (v) => {
            hasExplicitYearsRef.current = true;
            setYearFrom(v);
        },
        yearTo,
        setYearTo: (v) => {
            hasExplicitYearsRef.current = true;
            setYearTo(v);
        },
        stichmonat: effectiveStichmonat,
        setStichmonat,
        referenceComplete,
        sectors,
        setSectors: (v) => {
            hasExplicitSectorsRef.current = true;
            setSectors(v);
        },
        reps,
        setReps,
        articleTypes,
        setArticleTypes,
        productLines,
        setProductLines,
        articles,
        setArticles,
        withoutFreight,
        setWithoutFreight,
        params,
    };
}
